import { createTheme, Theme, ThemeOptions } from "@mui/material";
import {
  error,
  info,
  primary,
  secondary,
  success,
  surface,
  warning,
} from "./themeColors";

const fontFamily = "'Montserrat', sans-serif";

const baseOptions: ThemeOptions = {
  direction: "ltr",
  breakpoints: {
    values: { xs: 0, sm: 600, md: 960, lg: 1280, xl: 1920 },
  },
  palette: {
    primary,
    secondary,
    error,
    warning,
    success,
    info,
    divider: secondary[300],
    background: { default: surface.page, paper: surface.paper },
    text: {
      primary: secondary[500],
      secondary: secondary[400],
      disabled: secondary[300],
    },
    mode: "light",
  },
  typography: {
    fontFamily,
    fontSize: 14,
    h1: { fontWeight: 800, fontSize: "2.5rem" },
    h2: { fontWeight: 800, fontSize: "2rem" },
    h3: { fontWeight: 700, fontSize: "1.75rem" },
    h4: { fontWeight: 700, fontSize: "1.5rem" },
    h5: { fontWeight: 700, fontSize: "1.25rem" },
    h6: { fontWeight: 700, fontSize: "1rem" },
    subtitle1: { fontWeight: 600, fontSize: 14 },
    subtitle2: { fontWeight: 600, fontSize: 13 },
    body1: { fontSize: 14 },
    body2: { fontSize: 13 },
    caption: { fontSize: 12 },
    overline: { fontWeight: 700, letterSpacing: "0.08em" },
    button: { fontWeight: 600, textTransform: "none" },
  },
  shape: { borderRadius: 10 },
  shadows: [
    "none",
    "0px 1px 3px rgba(15,23,42,0.08), 0px 1px 2px rgba(15,23,42,0.04)",
    "0px 2px 6px rgba(15,23,42,0.08), 0px 1px 4px rgba(15,23,42,0.04)",
    "0px 4px 12px rgba(15,23,42,0.08), 0px 2px 6px rgba(15,23,42,0.04)",
    "0px 6px 16px rgba(15,23,42,0.10), 0px 3px 8px rgba(15,23,42,0.05)",
    "0px 8px 20px rgba(15,23,42,0.10), 0px 4px 10px rgba(15,23,42,0.05)",
    "0px 10px 24px rgba(15,23,42,0.10)",
    "0px 12px 28px rgba(15,23,42,0.10)",
    "0px 14px 32px rgba(15,23,42,0.10)",
    "0px 16px 36px rgba(15,23,42,0.10)",
    "0px 18px 40px rgba(15,23,42,0.10)",
    "0px 20px 44px rgba(15,23,42,0.10)",
    "0px 22px 48px rgba(15,23,42,0.10)",
    "0px 24px 52px rgba(15,23,42,0.10)",
    "0px 26px 56px rgba(15,23,42,0.10)",
    "0px 28px 60px rgba(15,23,42,0.10)",
    "0px 30px 64px rgba(15,23,42,0.10)",
    "0px 32px 68px rgba(15,23,42,0.10)",
    "0px 34px 72px rgba(15,23,42,0.10)",
    "0px 36px 76px rgba(15,23,42,0.10)",
    "0px 38px 80px rgba(15,23,42,0.10)",
    "0px 40px 84px rgba(15,23,42,0.10)",
    "0px 42px 88px rgba(15,23,42,0.10)",
    "0px 44px 92px rgba(15,23,42,0.10)",
    "0px 46px 96px rgba(15,23,42,0.10)",
  ] as any,
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        "*": { boxSizing: "border-box" },
        html: {
          height: "100%",
          width: "100%",
          MozOsxFontSmoothing: "grayscale",
          WebkitFontSmoothing: "antialiased",
        },
        body: { height: "100%", fontFamily, backgroundColor: surface.page },
        "#root": { height: "100%" },
        a: { textDecoration: "none", color: "inherit" },
        "#nprogress .bar": {
          zIndex: "9999 !important",
          backgroundColor: `${primary.main} !important`,
          width: "100%",
          position: "fixed",
        },
        "input[type=number]::-webkit-outer-spin-button, input[type=number]::-webkit-inner-spin-button":
          { WebkitAppearance: "none", margin: 0 },
        "::-webkit-scrollbar": { width: 6, height: 6 },
        "::-webkit-scrollbar-track": { background: surface.page },
        "::-webkit-scrollbar-thumb": {
          background: "#CBD5E1",
          borderRadius: 4,
        },
        "::-webkit-scrollbar-thumb:hover": { background: secondary[400] },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          borderRadius: 8,
          boxShadow: "none",
          fontWeight: 600,
          fontSize: 13,
          "&:hover": { boxShadow: "none" },
        },
        containedPrimary: {
          background: `linear-gradient(135deg, ${primary.main} 0%, ${primary[500]} 100%)`,
          color: "#fff",
          "&:hover": {
            background: `linear-gradient(135deg, ${primary.dark} 0%, ${primary.main} 100%)`,
          },
        },
        outlinedPrimary: { borderColor: primary.main, color: primary.main },
        sizeLarge: { padding: "10px 24px", fontSize: 14 },
        sizeSmall: { padding: "4px 12px", fontSize: 12 },
      },
    },
    MuiButtonBase: {
      styleOverrides: { root: { fontFamily } },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: "0px 2px 8px rgba(15,23,42,0.06)",
          border: `1px solid ${surface.border}`,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: { backgroundImage: "none" },
        rounded: { borderRadius: 12 },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { fontWeight: 600, fontSize: 12, borderRadius: 6 },
        sizeSmall: { height: 22 },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          "& .MuiTableCell-head": {
            fontWeight: 700,
            fontSize: 12,
            textTransform: "uppercase",
            letterSpacing: "0.05em",
            backgroundColor: surface.subtle,
            color: secondary[400],
            borderBottom: `2px solid ${surface.border}`,
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: { fontSize: 13, borderColor: surface.borderSubtle },
      },
    },
    MuiTableBody: {
      styleOverrides: {
        root: {
          "& .MuiTableRow-root:last-of-type": {
            "& .MuiTableCell-root": { borderBottom: 0 },
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            borderRadius: 8,
            fontSize: 13,
            backgroundColor: surface.paper,
            "& fieldset": { borderColor: surface.border },
            "&:hover fieldset": { borderColor: primary.main },
          },
          "& .MuiInputLabel-root": { fontSize: 13, color: secondary[400] },
          "& input::placeholder": { color: secondary[400], opacity: 1 },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: { borderRadius: 8, backgroundColor: surface.paper },
      },
    },
    MuiLinearProgress: {
      styleOverrides: { root: { borderRadius: 4, height: 6 } },
    },
    MuiAvatar: {
      styleOverrides: {
        root: { fontWeight: 700 },
        fallback: { height: "75%", width: "75%" },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          fontSize: 12,
          fontWeight: 500,
          backgroundColor: secondary[500],
          borderRadius: 6,
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: { borderBottom: `1px solid ${surface.border}`, minHeight: 42 },
        indicator: { height: 3, borderRadius: "3px 3px 0 0" },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: "none",
          fontWeight: 600,
          fontSize: 13,
          minHeight: 42,
          minWidth: "auto",
          color: secondary[400],
          padding: "0 16px",
          "&.Mui-selected": { color: primary.main },
        },
      },
    },
    MuiDialog: {
      styleOverrides: { paper: { borderRadius: 16 } },
    },
    // Dialog chrome is set once here so every dialog in the app — page dialogs,
    // the shared ModalDialog, confirm prompts — is spaced and divided the same.
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontWeight: 700,
          fontSize: 17,
          padding: "18px 24px",
          borderBottom: `1px solid ${surface.borderSubtle}`,
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: { root: { padding: "24px !important" } },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: "14px 24px",
          borderTop: `1px solid ${surface.borderSubtle}`,
          gap: 8,
        },
      },
    },
    MuiAlert: {
      styleOverrides: { root: { borderRadius: 10, fontSize: 13 } },
    },
    MuiPopover: {
      styleOverrides: {
        root: {
          "& .MuiPopover-paper": {
            borderRadius: 10,
            border: `1px solid ${surface.border}`,
            boxShadow: "0px 8px 24px rgba(15,23,42,0.10)",
          },
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: 10,
          border: `1px solid ${surface.border}`,
          boxShadow: "0px 8px 24px rgba(15,23,42,0.10)",
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: { root: { fontSize: 13 } },
    },
    MuiRating: {
      styleOverrides: { root: { color: warning.main } },
    },
  },
};

export type themeSettingsProps = {
  theme: string;
  direction: string;
  responsiveFontSizes: boolean;
};

export const parishTheme = (): Theme => createTheme({ ...baseOptions });

/** Legacy alias — the app called this `ukoTheme` before the redesign. */
export const ukoTheme = parishTheme;
